import { z } from 'zod';
// Create validation and DTO type
export const agendaCreateSchema = z.object({
    agendaTemplateId: z.number().nullish(),
    createAssociatedItems: z.boolean().optional(),
});
const agendaSectionCreateBaseSchema = z.object({
    id: z.number().optional(),
    uuid: z.string().optional(),
    parentId: z.number().nullable().optional(),
    title: z.string().min(1, 'Title is required').max(255),
    description: z.string().nullish(),
    displayType: z.string().min(1, 'Display type is required').max(255),
    orderById: z.number().min(1, 'Order by is required'),
    automaticAssignmentRules: z.string().nullable(),
    sectionNumber: z.string().nullable().optional(),
    acceptsItems: z.boolean(),
    isConsentSection: z.boolean().optional(),
    restartsNumbering: z
        .boolean()
        .optional()
        .describe('Whether auto-numbering restarts at this section: it is numbered 1 (A, I, …) again and its following siblings continue from there.'),
    allowPublicSpeakers: z
        .boolean()
        .optional()
        .describe('Whether the public can sign up to speak on this section.'),
    allowItemPublicSpeakers: z
        .boolean()
        .optional()
        .describe('Whether the public can sign up to speak on the legislative items within this section.'),
    allowPublicComments: z
        .boolean()
        .optional()
        .describe('Whether the public can submit written comments on this section.'),
    allowItemPublicComments: z
        .boolean()
        .optional()
        .describe('Whether the public can submit written comments on the legislative items within this section.'),
    displayChildSectionsFirst: z
        .boolean()
        .optional()
        .describe('Whether child sections are displayed before the legislative items of this section.'),
    recommendedActionText: z.string().nullish(),
    internalNotes: z
        .string()
        .optional()
        .nullable()
        .describe('Notes for internal staff and meeting members. Never exposed publicly. Use HTML.'),
    actionIds: z.array(z.number()).optional(),
    defaultActionId: z.number().nullish(),
});
export const agendaSectionCreateSchema = agendaSectionCreateBaseSchema;
export const agendaSectionCreateSchemaWithChildren = agendaSectionCreateBaseSchema.extend({
    childSections: z.array(agendaSectionCreateBaseSchema.extend({
        childSections: z
            .array(agendaSectionCreateBaseSchema.extend({
            childSections: z
                .array(agendaSectionCreateBaseSchema.extend({
                childSections: z.array(agendaSectionCreateSchema).optional(),
            }))
                .optional(),
        }))
            .optional(),
    })),
});
export const agendaSectionsUpdateSchema = z.object({
    header: z.string().nullish(),
    footer: z.string().nullish(),
    sections: z.array(agendaSectionCreateSchemaWithChildren),
});
export const agendaGetItemsRecommendationsSchema = z.object({
    itemIds: z.array(z.number()),
});
export const agendaSectionsItemsUpdateSchema = z.object({
    sections: z.array(z.object({
        id: z.number(),
        items: z
            .array(z.object({
            legislativeItemId: z.number('Legislative item ID is required'),
            meetingBodyIds: z.array(z.number()),
        }))
            // an Item can be placed in several sections of the same agenda, but only once per section
            .refine((items) => new Set(items.map(({ legislativeItemId }) => legislativeItemId)).size === items.length, 'A Legislative Item can only be placed once in the same section'),
    })),
});
export const agendaSectionItemNumberUpdateSchema = z.object({
    agendaSectionId: z.number(),
    legislativeItemId: z.number(),
    itemNumber: z.string(),
});
export const agendaNumberUpdateSchema = z.object({
    type: z.enum(['section', 'item']),
    sectionId: z.number(),
    itemId: z.number().optional(),
    number: z.string(),
});
export const quickAddLegislativeItemSchema = z.object({
    fullTitle: z.string().min(1, 'Full title is required'),
    summary: z.string().optional().nullable(),
    legislativeTypeId: z.number().min(1, 'Legislative type is required'),
    meetingBodyIds: z.array(z.number()).min(1, 'At least one meeting body is required'),
    agendaSectionId: z.number().min(1, 'Agenda section is required'),
    staffContactIds: z.array(z.number().int().positive()).max(50).optional(),
    departmentId: z.number().optional(),
});
export const agendaOutlineDocumentCreateSchema = z.object({
    docx: z.boolean().optional(),
});
export const agendaPackageCreateSchema = z.object({
    includePublicComments: z.boolean().optional(),
});
export const agendaPublishUpdateSchema = z.object({
    incrementVersion: z
        .boolean()
        .describe('Whether the update counts as a new publicly advertised version. When false the change is acknowledged without bumping the version or notifying subscribers.'),
});
export const agendaPublishSchema = z.object({
    incrementVersion: z
        .boolean()
        .optional()
        .describe('Required when republishing an agenda that changed since it was last public: whether those changes count as a new publicly advertised version.'),
    notifySubscribers: z
        .boolean()
        .optional()
        .describe('Whether to email subscribers about the publication. Defaults to true.'),
});
